import { Intent as BlueprintIntent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Input,
  LoadingIcon,
  Modal,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  DELETE_ENDPOINT_DIALOG_CANCEL,
  DELETE_ENDPOINT_DIALOG_INPUT,
  DELETE_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnections } from 'src/ui/containers/endpoints/utils/invalidate';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useDeleteMyOrganizationConnectionMutation } from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const DELETE_CONFIRMATION = 'DELETE';

const isOpenSelector = isDialogOpenSelector(
  DialogType.DELETE_MY_ORGANIZATION_ENDPOINT
);

export const DeleteMyOrgEndpointDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { connection } = useCurrentConnection(editIndex.toString(), org);
  const invalidateConnections = useInvalidateConnections(org);

  const [value, setValue] = React.useState('');

  const {
    isLoading,
    mutate: deleteMyOrgConnection,
  } = useDeleteMyOrganizationConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateConnections();

      if (!data.deleteMyOrganizationConnection?.success) {
        dispatch(
          queueNotification(
            data.deleteMyOrganizationConnection?.error ||
              copy.components.endpoints.deleteEndpointDialog.notifications.error
          )
        );
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.deleteEndpointDialog.notifications
              .success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setValue('');
  };

  const handleSubmit = () => {
    if (value.trim() !== DELETE_CONFIRMATION || isLoading || !connection?.id) {
      return;
    }

    deleteMyOrgConnection({
      input: {
        id: connection.id,
        org_domain: org,
      },
    });
  };

  const isSubmitDisabled = value.trim() !== DELETE_CONFIRMATION || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={DELETE_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.deleteEndpointDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={DELETE_ENDPOINT_DIALOG_SUBMIT}
            variant="primary-gray"
          >
            {copy.components.endpoints.deleteEndpointDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.deleteEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <Body size="md">
          {copy.components.endpoints.deleteEndpointDialog.description.pre}{' '}
          <strong>{connection?.name}</strong>{' '}
          {copy.components.endpoints.deleteEndpointDialog.description.post(
            connection?.endpoint || ''
          )}
        </Body>

        <Input
          autoFocus
          clearable={false}
          label={copy.components.endpoints.inputs.delete.label}
          onChange={(e) => setValue(e.target.value)}
          size="md"
          testId={DELETE_ENDPOINT_DIALOG_INPUT}
          value={value}
        />
      </div>
    </Modal>
  );
};
